// src/components/admin/AdminTransferStatsPage.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Container, Grid, Paper, Typography, Box, Card,
  IconButton, Button, Chip, Skeleton, Alert, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp, Phone, Business, CheckCircle,
  Cancel, Schedule, Assessment, Refresh, Visibility
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';
import StatCard from '../common/StatCard';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.6
};

export default function AdminTransferStatsPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTransferData();
  }, []);

  const fetchTransferData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [statsData, logsData] = await Promise.all([
        adminApi.getTransferStats(),
        adminApi.getTransferLogs()
      ]);
      setStats(statsData);
      setLogs(logsData.results || logsData || []);
    } catch (err) {
      console.error('Error fetching transfer data:', err);
      setError(err.message || 'فشل في تحميل بيانات التحويل');
      // تعيين قيم افتراضية في حالة الخطأ
      setStats({
        total_transfers: 0,
        successful_transfers: 0,
        failed_transfers: 0,
        transfers_today: 0,
        success_rate: 0,
        failure_rate: 0,
        avg_response_time: 0,
        department_performance: [],
        transfer_types: [],
        transfer_statuses: [],
        daily_trends: []
      });
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle color="success" />;
      case 'failed':
        return <Cancel color="error" />;
      case 'pending':
        return <Schedule color="warning" />;
      default:
        return <Visibility />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="text" width="60%" height={32} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item key={item} xs={12} sm={6} md={3}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="700" gutterBottom>
              إحصائيات نظام التحويل
            </Typography>
            <Typography variant="body1" color="text.secondary">
              مراقبة أداء نظام التحويل والإشعارات
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchTransferData}
          >
            تحديث البيانات
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="إجمالي التحويلات"
              value={stats?.total_transfers || 0}
              icon={<TrendingUp />}
              color="primary"
              trend="up"
              trendValue={stats?.transfers_today || 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="التحويلات الناجحة"
              value={stats?.successful_transfers || 0}
              icon={<CheckCircle />}
              color="success"
              trend="up"
              trendValue={stats?.success_rate || 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="التحويلات الفاشلة"
              value={stats?.failed_transfers || 0}
              icon={<Cancel />}
              color="error"
              trend="down"
              trendValue={stats?.failure_rate || 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="متوسط وقت الاستجابة"
              value={`${stats?.avg_response_time || 0} دقيقة`}
              icon={<Schedule />}
              color="info"
              trend="up"
              trendValue={stats?.response_time_trend || 0}
            />
          </Grid>
        </Grid>

        {/* Department Performance */}
        {stats?.department_performance && (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              أداء الأقسام
            </Typography>
            <Grid container spacing={2}>
              {stats.department_performance.map((dept) => (
                <Grid item key={dept.id} xs={12} sm={6} md={4}>
                  <Card sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Business color="primary" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight="600">
                          {dept.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {dept.total_transfers} تحويل
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        معدل النجاح
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={dept.success_rate || 0}
                        sx={{ height: 8, borderRadius: 4 }}
                        color={dept.success_rate >= 80 ? 'success' : dept.success_rate >= 60 ? 'warning' : 'error'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {dept.success_rate || 0}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        label={`${dept.active_phones} رقم نشط`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={`${dept.avg_response_time || 0} دقيقة`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Transfer Logs */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            سجلات التحويل الأخيرة
          </Typography>
          
          {logs.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>القسم</TableCell>
                    <TableCell>المستخدم</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>وقت الاستجابة</TableCell>
                    <TableCell>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.slice(0, 10).map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(log.created_at).toLocaleDateString('ar-SA')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(log.created_at).toLocaleTimeString('ar-SA')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Business fontSize="small" />
                          <Typography variant="body2">
                            {log.department_name}
                          </Typography>
                        </Box>
                      </TableCell>
                                             <TableCell>
                         <Typography variant="body2">
                           {log.user_phone_number || log.conversation_phone || 'غير متوفر'}
                         </Typography>
                       </TableCell>
                      <TableCell>
                                                 <Chip
                           label={log.status === 'success' ? 'نجح' : log.status === 'failed' ? 'فشل' : log.status === 'pending' ? 'معلق' : log.status}
                           color={getStatusColor(log.status)}
                           size="small"
                           icon={getStatusIcon(log.status)}
                         />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.response_time || 0} دقيقة
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                لا توجد سجلات تحويل
              </Typography>
              <Typography variant="body2" color="text.secondary">
                سيتم عرض سجلات التحويل هنا عند حدوث تحويلات
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Quick Actions */}
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            إجراءات سريعة
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Business />}
                onClick={() => navigate('/admin/departments')}
                sx={{ py: 2 }}
              >
                إدارة الأقسام
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Phone />}
                onClick={() => navigate('/admin/departments')}
                sx={{ py: 2 }}
              >
                إدارة أرقام الهاتف
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Assessment />}
                onClick={() => navigate('/admin/logs')}
                sx={{ py: 2 }}
              >
                سجلات النظام
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchTransferData}
                sx={{ py: 2 }}
              >
                تحديث البيانات
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </motion.div>
  );
}
